import { useCallback, useEffect, useState } from "react";
import { useInstalledExtensions } from "../../../hooks/useInstalledExtensions";
import { Extension } from "../../../models/intalled-extensions";
import { useExtensionConfigs } from "../../../hooks/useExtensionConfigs";
import { IExtensionConfig } from "../../../models/extensions";
import { useTranslation } from "react-i18next";
import { IExams } from "../../../models/exams";
import { IExamsWithPacsValidation } from "./models";

const translationPath = "page.exams.";

export const useHasExamPacsAction = () => {
  const { t } = useTranslation();

  const [hasPixeonExtension] = useInstalledExtensions(Extension.PIXEON);

  const [hasVueMotionExtension] = useInstalledExtensions(Extension.VUE_MOTION);

  const { fetchExtensionConfig } = useExtensionConfigs();

  const [pacsColumnName, setPacsColumnName] = useState(
    t(translationPath + "pacs"),
  );

  const [loadingPacsValues, setLoadingPacsValues] = useState(true);

  const [hasExamPacsAction, setHasExamPacsAction] = useState(false);

  const [iconDisplayFormat, setIconDisplayFormat] =
    useState<IExtensionConfig>();

  const [
    internaProcedureClassificationPixeon,
    setInternaProcedureClassificationPixeon,
  ] = useState<IExtensionConfig>();

  const [
    internaProcedureClassificationVue,
    setInternaProcedureClassificationVue,
  ] = useState<IExtensionConfig>();

  const canOpenPixeon = useCallback(
    (examsPacs: IExamsWithPacsValidation[]) => {
      const iconDisplayFormatIsAll =
        iconDisplayFormat?.value.toLowerCase() === "all";

      const examsPixeon: IExamsWithPacsValidation[] = examsPacs.map((exam) => {
        let iconDisplayFormatValidator = false;
        if (iconDisplayFormatIsAll) {
          const internalProcClassifCode =
            exam.internalProcClassifCode?.toString();
          iconDisplayFormatValidator = !!(
            internalProcClassifCode &&
            internaProcedureClassificationPixeon?.value.includes(
              internalProcClassifCode,
            )
          );
        } else {
          iconDisplayFormatValidator =
            iconDisplayFormat?.value ===
            exam.dicomAccesses[0].integrationDescription;
        }

        const matchExamType = exam.type === "NAO-LABORATORIAL";
        const validator = matchExamType && iconDisplayFormatValidator;

        return { ...exam, canShowPixeon: !!validator };
      });

      return examsPixeon;
    },
    [iconDisplayFormat, internaProcedureClassificationPixeon],
  );

  const canOpenVueMotion = useCallback(
    (examsPacs: IExamsWithPacsValidation[]) => {
      const examsVue: IExamsWithPacsValidation[] = examsPacs.map((exam) => {
        const internalProcClassifCode =
          exam.internalProcClassifCode?.toString();
        const matchExamType = exam.type === "NAO-LABORATORIAL";
        const validator =
          matchExamType &&
          internalProcClassifCode &&
          internaProcedureClassificationVue?.value.includes(
            internalProcClassifCode,
          );

        return { ...exam, canShowVueMotion: !!validator };
      });

      return examsVue;
    },
    [internaProcedureClassificationVue],
  );

  const handleFormatExams = useCallback(
    (data: IExams[]): IExams[] | IExamsWithPacsValidation[] => {
      const showVueMotionPacsUpdate =
        hasVueMotionExtension && canOpenVueMotion(data);
      const showPixeonPacsUpdate =
        hasPixeonExtension && canOpenPixeon(showVueMotionPacsUpdate || data);
      console.log(showPixeonPacsUpdate);

      return showPixeonPacsUpdate || showVueMotionPacsUpdate || data;
    },
    [
      hasPixeonExtension,
      hasVueMotionExtension,
      canOpenPixeon,
      canOpenVueMotion,
    ],
  );

  const fetchPacsExtensionConfigs = useCallback(async () => {
    if (hasPixeonExtension) {
      const iconDisplayFormatUpdate = await fetchExtensionConfig(
        Extension.PIXEON,
        "IconDisplayFormat",
      );
      const iconDisplayFormatIsAll =
        iconDisplayFormatUpdate?.value.toLowerCase() === "all";

      if (iconDisplayFormatIsAll) {
        const internaProcedureClassificationPixeonUpdate =
          await fetchExtensionConfig(
            Extension.PIXEON,
            "InternaProcedureClassification",
          );

        setInternaProcedureClassificationPixeon(
          internaProcedureClassificationPixeonUpdate,
        );
      }

      setIconDisplayFormat(iconDisplayFormatUpdate);
    }

    if (hasVueMotionExtension) {
      const internaProcedureClassificationVueUpdate =
        await fetchExtensionConfig(
          Extension.VUE_MOTION,
          "InternaProcedureClassification",
        );

      setInternaProcedureClassificationVue(
        internaProcedureClassificationVueUpdate,
      );
    }
  }, [fetchExtensionConfig, hasPixeonExtension, hasVueMotionExtension]);

  const fetchPacsValues = useCallback(async () => {
    try {
      const hasPacs = hasPixeonExtension || hasVueMotionExtension;
      if (!hasPacs) return;

      await fetchPacsExtensionConfigs();

      let pacsColumnNameUpdate: IExtensionConfig | undefined;
      if (hasPixeonExtension) {
        pacsColumnNameUpdate = await fetchExtensionConfig(
          Extension.PIXEON,
          "ColumnTitle",
        );
      } else if (hasVueMotionExtension) {
        pacsColumnNameUpdate = await fetchExtensionConfig(
          Extension.VUE_MOTION,
          "ColumnTitle",
        );
      }

      setHasExamPacsAction(hasPacs);
      setPacsColumnName((prev) => pacsColumnNameUpdate?.value ?? prev);
    } finally {
      setLoadingPacsValues(false);
    }
  }, [
    hasPixeonExtension,
    hasVueMotionExtension,
    fetchExtensionConfig,
    fetchPacsExtensionConfigs,
  ]);

  useEffect(() => {
    fetchPacsValues();
  }, [fetchPacsValues]);

  return {
    hasPixeonExtension,
    hasVueMotionExtension,
    hasExamPacsAction,
    loadingPacsValues,
    pacsColumnName,
    handleFormatExams,
  };
};
